import { observer } from 'mobx-react-lite';
import { type SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import {
  IconButton,
  SIZE,
  type Size,
  VARIANT,
} from '~/components/isolated/IconButton';
import type { ProductViewProps } from '~/pages/Product/Product';
import { userStore } from '~/stores';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import copyToClipboard from '~/utils/copyToClipboard';

import styles from './MiscButtons.module.scss';

const MiscButtons = ({
  product,
  handleFavorite,
  size = SIZE.XL,
  isLoading,
  isVertical = false,
  className,
}: Pick<
  ProductViewProps,
  'product' | 'handleFavorite' | 'isLoading' | 'className'
> & {
  size?: Size;
  isVertical?: boolean;
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<Nullable<ReturnType<typeof setTimeout>>>(null);

  const isFavorite =
    product.data?.id && favoritesStore.isFavorited(product.data.id);

  const onFavorite = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleFavorite();
  };

  const handleCopy = () => {
    const url = encodeURI(document.location.href);
    copyToClipboard({ text: url, successMessage: t('linkCopied') }).then(() => {
      setCopied(true);

      copyTimeoutRef.current = setTimeout(() => {
        setCopied(false);
      }, 1500);
    });
  };

  useEffect(
    () => () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    },
    [],
  );

  if (isLoading) {
    return (
      <div
        className={cn(
          styles.iconButtons,
          isVertical && styles.vertical,
          className,
        )}
      >
        <Skeleton containerClassName={styles.skeleton} />
        <Skeleton containerClassName={styles.skeleton} />
      </div>
    );
  }
  return (
    <div
      className={cn(
        styles.iconButtons,
        isVertical && styles.vertical,
        className,
      )}
    >
      {(!company.hideFavoritesForGuests || userStore.isAuthorized) && (
        <IconButton
          variant={company.match({
            mcosmetic: isVertical ? VARIANT.shadowXS : VARIANT.default,
            default: VARIANT.default,
          })}
          size={size}
          onClick={onFavorite}
          icon={isFavorite ? 'heart-fill' : 'heart'}
          className={isFavorite ? styles.favoriteActive : ''}
        />
      )}
      <IconButton
        size={size}
        variant={company.match({
          mcosmetic: isVertical ? VARIANT.shadowXS : VARIANT.default,
          default: VARIANT.default,
        })}
        icon={copied ? 'check' : 'link'}
        onClick={handleCopy}
      />
    </div>
  );
};

export default observer(MiscButtons);
